import { Link } from 'react-router-dom';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { MinusSmallIcon, PlusSmallIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

const faqGroups = [
  {
    heading: 'Getting started',
    faqs: [
      {
        question: 'How do I create my first game?',
        answer: 'Go to Campaigns and click "Create Campaign". Choose a game type (e.g. Snake, Flappy, Runner), then customize the look and settings. When you\'re happy, publish the campaign and use the share link or QR code to get players in.',
      },
      {
        question: 'How do I share a game with players?',
        answer: 'Once a campaign is published, open it and use the "Share Game" button to copy the link, or open "QR Code" in the same menu and use "Download QR" to get a QR code. For a kiosk or TV, open the share link in a new tab (copy the link, then paste in a full-screen browser). Share the link or show the QR so players can play on their phones.',
      },
    ],
  },
  {
    heading: 'Campaigns & games',
    faqs: [
      {
        question: 'What\'s the difference between Draft and Published?',
        answer: 'Draft campaigns are only visible to you and can be edited freely. Published campaigns are live: players can open the share link and play. You can still edit published campaigns; changes apply to the live game. Use "Pause" to temporarily stop access or "Complete" when an event/campaign ends.',
      },
      {
        question: 'Can I customize colours, logo, and branding on my games?',
        answer: 'Yes. When creating or editing a campaign, use the appearance/customize step to pick themes, upload your logo, set colours, and choose fonts. You can also save custom styles for reuse across campaigns.',
      },
      {
        question: 'How do leaderboards work?',
        answer: 'You can turn on a leaderboard for any campaign. Players who submit a score can appear on the board (with optional name and email). You can manage visibility and reset the board from the campaign\'s leaderboard section.',
      },
      {
        question: 'Can I capture leads or emails from players?',
        answer: 'Yes. When editing a campaign, you can enable email capture in the campaign settings (e.g. in the leaderboard step). Players can optionally enter their email to appear on the leaderboard or for follow-up. You can view and export leads from the campaign\'s leaderboard or entries section.',
      },
      {
        question: 'What happens when I pause or complete a campaign?',
        answer: 'Paused campaigns show a "game unavailable" message to players but keep the link/QR alive \u2014 you can resume anytime at no cost. Completed campaigns mark the campaign as finished: they\'re hidden from your main list and the public link stops working. Both preserve your play data and leads.',
      },
      {
        question: 'Where can I see play stats and analytics?',
        answer: 'Open any campaign to see plays, CTA clicks, entries (leads), and a chart over time. The Dashboard shows an overview: play counts per campaign and a recent-activity chart. On the campaign page you also get leaderboard data when the leaderboard is enabled.',
      },
    ],
  },
  {
    heading: 'Account & billing',
    faqs: [
      {
        question: 'How do credits and billing work?',
        answer: 'Free to build, preview, and edit. Publishing a game live costs \u00a339 per credit (or \u00a3149 for a 5-game pack). Buy credits from the sidebar, the inline publish modal, or Settings \u2192 Plan & billing. Editing a published game stays free forever \u2014 no subscription, no recurring charges.',
      },
      {
        question: 'How do I get a refund?',
        answer: 'Email support@smilesflow.com with your purchase reference and we\u2019ll handle it manually. Aim is a same-working-day reply.',
      },
    ],
  },
  {
    heading: 'Data & privacy',
    faqs: [
      {
        question: 'Where is my data stored? Can I export it?',
        answer: 'Data is stored securely and processed in line with our Privacy Policy (see the link in the footer or your account). You can export leads from a campaign (leaderboard/entries). For a full copy of your data, contact us.',
      },
    ],
  },
];

const SUPPORT_EMAIL = 'support@smilesflow.com';

export default function FeedbackPanel() {
  return (
    <div className="w-full px-8 py-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold tracking-tight gch-text-primary">
          Get Help
        </h2>
        <p className="text-sm gch-text-secondary mt-1">
          Common questions about SmilesFlow. Can&apos;t find what you need? Contact us below.
        </p>
      </div>

      <div className="space-y-8">
        {faqGroups.map((group) => (
          <section key={group.heading}>
            <h3 className="text-xs font-semibold uppercase tracking-wider gch-text-muted mb-3">
              {group.heading}
            </h3>
            <dl className="space-y-0 rounded-xl border border-[var(--admin-border)] overflow-hidden divide-y divide-[var(--admin-border)] bg-white/5">
              {group.faqs.map((faq) => (
                <Disclosure as="div" key={faq.question}>
                  {({ open }) => (
                    <>
                      <dt>
                        <DisclosureButton className="flex w-full items-start justify-between text-left gch-text-primary py-3.5 px-4 hover:bg-white/5 transition-colors gap-3">
                          <span className="text-sm font-semibold leading-snug pr-2">{faq.question}</span>
                          <span className="flex h-5 w-5 items-center justify-center shrink-0 rounded-full bg-white/10">
                            {open ? (
                              <MinusSmallIcon className="h-3.5 w-3.5 gch-text-muted" aria-hidden="true" />
                            ) : (
                              <PlusSmallIcon className="h-3.5 w-3.5 gch-text-muted" aria-hidden="true" />
                            )}
                          </span>
                        </DisclosureButton>
                      </dt>
                      <DisclosurePanel as="dd" className="px-4 pt-2 pb-4">
                        <p className="text-sm leading-relaxed gch-text-secondary">{faq.answer}</p>
                      </DisclosurePanel>
                    </>
                  )}
                </Disclosure>
              ))}
            </dl>
          </section>
        ))}
      </div>

      {/* Resources */}
      <div className="mt-8 pt-6 border-t border-[var(--admin-border)]">
        <p className="text-sm gch-text-secondary">
          <Link to="/privacy" className="text-[var(--admin-accent)] hover:underline">Privacy Policy</Link>
          {' \u00b7 '}
          <Link to="/terms" className="text-[var(--admin-accent)] hover:underline">Terms of Service</Link>
        </p>
      </div>

      {/* Contact */}
      <div className="mt-6 pt-6 border-t border-[var(--admin-border)]">
        <h3 className="text-sm font-semibold gch-text-primary mb-1">Contact us</h3>
        <p className="text-sm gch-text-secondary mb-3">
          For support, billing, or partnership enquiries:
        </p>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold btn-admin-outline"
        >
          <EnvelopeIcon className="w-4 h-4" />
          {SUPPORT_EMAIL}
        </a>
      </div>
    </div>
  );
}
